import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Switch, Slider, message, Card } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getDeduplicationRule,
  createDeduplicationRule,
  updateDeduplicationRule,
  DeduplicationMatchStrategies,
  DeduplicationMergeStrategies,
  DeduplicationMatchTypes,
  getDeduplicationMatchFields,
} from '@/api/deduplicationApi';
import { DataManagementModules } from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';

export default function DeduplicationRuleForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const isEdit = !!id;
  const canWrite = hasPermission('data_management', 'write');

  useEffect(() => {
    if (!canWrite) navigate('/data-management/dedup-rules');
  }, [canWrite, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getDeduplicationRule(id);
        if (res.data?.success && res.data.data) {
          form.setFieldsValue(res.data.data);
          loadFieldsForModule(res.data.data.target_module);
        }
      } catch {
        message.error(t('app.error'));
      }
    })();
  }, [id, form, t]);

  const loadFieldsForModule = async (module: string) => {
    try {
      const res = await getDeduplicationMatchFields(module);
      if (res.data?.success && res.data.data) {
        setAvailableFields(res.data.data);
      }
    } catch {
      setAvailableFields([]);
    }
  };

  const onModuleChange = (module: string) => {
    loadFieldsForModule(module);
    form.setFieldsValue({ match_fields: [] });
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateDeduplicationRule(id!, values);
        message.success(t('data_management.dedup.rule.update_success'));
      } else {
        await createDeduplicationRule(values);
        message.success(t('data_management.dedup.rule.create_success'));
      }
      navigate('/data-management/dedup-rules');
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          match_strategy: 'all_fields',
          merge_strategy: 'keep_newest',
          similarity_threshold: 0.8,
          is_active: true,
          auto_merge: false,
          match_fields: [],
        }}
      >
        <Form.Item
          name="name"
          label={t('data_management.dedup.rule.field.name')}
          rules={[{ required: true, max: 200 }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('data_management.dedup.rule.field.description')}
          rules={[{ max: 1000 }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="target_module"
            label={t('data_management.dedup.rule.field.target_module')}
            rules={[{ required: true }]}
          >
            <Select
              onChange={onModuleChange}
              options={DataManagementModules.map((m) => ({
                value: m,
                label: t(`data_management.import.modules.${m}`),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="match_strategy"
          label={t('data_management.dedup.rule.field.match_strategy')}
        >
          <Select
            options={[...DeduplicationMatchStrategies].map((s) => ({
              value: s,
              label: t(`data_management.dedup.rule.match_strategies.${s}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="merge_strategy"
          label={t('data_management.dedup.rule.field.merge_strategy')}
        >
          <Select
            options={[...DeduplicationMergeStrategies].map((s) => ({
              value: s,
              label: t(`data_management.dedup.rule.merge_strategies.${s}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="similarity_threshold"
          label={t('data_management.dedup.rule.field.similarity_threshold')}
        >
          <Slider min={0} max={1} step={0.05} marks={{ 0: '0', 0.5: '0.5', 1: '1' }} />
        </Form.Item>

        <Form.Item
          name="is_active"
          label={t('data_management.dedup.rule.field.is_active')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="auto_merge"
          label={t('data_management.dedup.rule.field.auto_merge')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Card title={t('data_management.dedup.rule.field.match_fields')} style={{ marginBottom: 24 }}>
          <Form.List name="match_fields">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'field_name']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder={t('data_management.dedup.rule.field.field_name', 'Field')}
                        style={{ width: 160 }}
                        options={availableFields.map((f) => ({ value: f, label: f }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'match_type']}
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder={t('data_management.dedup.rule.field.match_type', 'Match Type')}
                        style={{ width: 140 }}
                        options={[...DeduplicationMatchTypes].map((mt) => ({
                          value: mt,
                          label: t(`data_management.dedup.rule.match_types.${mt}`),
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'weight']}
                      initialValue={1}
                    >
                      <Select
                        placeholder={t('data_management.dedup.rule.field.weight', 'Weight')}
                        style={{ width: 80 }}
                        options={[1, 2, 3, 4, 5].map((w) => ({ value: w, label: String(w) }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'case_sensitive']}
                      valuePropName="checked"
                      initialValue={false}
                    >
                      <Switch size="small" checkedChildren="CS" unCheckedChildren="CI" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('data_management.dedup.rule.field.match_fields')}
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? t('app.save') : t('app.create')}
            </Button>
            <Button onClick={() => navigate('/data-management/dedup-rules')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
